import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { TOAST_DISMISS_MS } from '../constants';

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
  /** Optional extra testid for a caller that needs a stable, feature-specific
   *  selector (e.g. `incident-created-toast`) in addition to the generic
   *  `toast-success`/`toast-error` every toast already carries. */
  testId?: string;
}

interface ToastContextValue {
  showSuccess: (message: string, testId?: string) => void;
  showError: (message: string, testId?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (message: string, type: 'success' | 'error', testId?: string) => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { id, message, type, testId }]);
      setTimeout(() => removeToast(id), TOAST_DISMISS_MS);
    },
    [removeToast]
  );

  const showSuccess = useCallback(
    (message: string, testId?: string) => addToast(message, 'success', testId),
    [addToast]
  );
  const showError = useCallback(
    (message: string, testId?: string) => addToast(message, 'error', testId),
    [addToast]
  );

  return (
    <ToastContext.Provider value={{ showSuccess, showError }}>
      {children}
      <div data-testid="toast" className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            data-testid={toast.testId ?? `toast-${toast.type}`}
            data-toast-type={`toast-${toast.type}`}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-sm font-medium text-white min-w-[280px] max-w-md transition-all ${
              toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
            }`}
          >
            <span className="flex-1">{toast.message}</span>
            <button
              onClick={() => removeToast(toast.id)}
              className="text-white/80 hover:text-white flex-shrink-0"
            >
              &times;
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
